import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useRouter } from 'next/router';
import { t } from 'react-switch-lang';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@awesome.me/kit-3f20e419a7/icons/classic/solid';
import Image from 'next/image';
import { events, logAmpEvent } from '../utils/Amplitude';
import styles from '../styles/MobileAppPopup.module.scss';
import Button from './Button';
import { ExternalLink } from './Links';
import { formatAsTestId } from '../utils/Helpers';

const LOCALSTORAGE_KEY = 'mobilePopupLastShown';

export default function MobileAppPopup() {
  const router = useRouter();

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(navigator.userAgent);
    if (!isMobile) return;

    const today = new Date().toISOString().split('T')[0];
    const lastShown = localStorage.getItem(LOCALSTORAGE_KEY);

    if (lastShown !== today) {
      setShowModal(true);
      localStorage.setItem(LOCALSTORAGE_KEY, today);
      logAmpEvent(events.USER_VIEWED_MOBILE_APP_POPUP, { Page: router.asPath });
    }
  }, [router]);

  const closeModal = () => {
    logAmpEvent(events.USER_CLOSED_MOBILE_APP_POPUP);
    setShowModal(false);
  };

  return (
    <Modal className={styles.modal} show={showModal} onHide={closeModal}>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.topRow}>
          <Image
            src="/favicon.ico"
            alt="PaySimply logo"
            style={{ objectFit: 'contain' }}
            fill
            height={0}
            width={0}
            unoptimized
          />
          <Button
            fill="outline"
            onClick={closeModal}
            className={styles.modalCloseButton}
            ampEvent={events.USER_CLOSED_MOBILE_APP_POPUP}
            ariaLabel={t('MobileAppPopup_Close_ButtonDescription')}
            testId={formatAsTestId('mobile app popup close', 'button')}
          >
            <FontAwesomeIcon icon={faClose} size="lg" />
          </Button>
        </div>

        <h1>{t('MobileAppPopup_Title')}</h1>
        <p>{t('MobileAppPopup_Subtitle')}</p>

        <div className={styles.appLinksContainer}>
          <ExternalLink
            href={t('Link_PaySimplyMobileiOSApp')}
            aria-label={t('PaySimplyMobileApp_AppStore_LinkDescription')}
            ampEvent={events.USER_CLICKED_MOBILE_APP_POPUP_DOWNLOAD}
            ampEventProps={{ Store: 'App Store' }}
            testId={formatAsTestId('mobile app popup app store', 'external link')}
          >
            <Image
              src="/images/logos/AppStore.png"
              height={45}
              width={155}
              alt={t('PaySimplyMobileApp_AppStore_ImageDescription')}
            />
          </ExternalLink>
          <ExternalLink
            href={t('Link_PaySimplyMobileAndroidApp')}
            aria-label={t('PaySimplyMobileApp_PlayStore_LinkDescription')}
            ampEvent={events.USER_CLICKED_MOBILE_APP_POPUP_DOWNLOAD}
            ampEventProps={{ Store: 'Google Play' }}
            testId={formatAsTestId('mobile app popup google play', 'external link')}
          >
            <Image
              src="/images/logos/PlayStore.png"
              height={45}
              width={155}
              alt={t('PaySimplyMobileApp_PlayStore_ImageDescription')}
            />
          </ExternalLink>
        </div>

        <div className={styles.orBlock}>
          <span />
          <p>{t('MobileAppPopup_OrDivider')}</p>
          <span />
        </div>

        <Button
          linkBtn
          onClick={closeModal}
          className={styles.continueButton}
          ampEvent={events.USER_CLOSED_MOBILE_APP_POPUP}
          ariaLabel={t('MobileAppPopup_Continue_ButtonDescription')}
          text={t('MobileAppPopup_Continue')}
          testId={formatAsTestId('mobile app popup continue', 'button')}
        />
      </Modal.Body>
    </Modal>
  );
}
